import React, { useState, useEffect, useCallback } from 'react';
import ThirdPartyPortal from "./pages/ThirdPartyPortal";
import MLSAgentPortal from "./pages/MLSAgentPortal";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';

// Components
import Navbar from './components/Navbar';
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import BuyerPortal from './pages/BuyerPortal';
import SellerPortal from './pages/SellerPortal';
import AttorneyPortal from './pages/AttorneyPortal';
import Listings from './pages/Listings';
import ListingDetails from './pages/ListingDetails';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import WelcomeScreen from './pages/WelcomeScreen';
import VerificationComplete from './pages/VerificationComplete';
import BuyerOfferForm from './components/BuyerOfferForm';
import PublicOfferPage from './pages/PublicOfferPage';
import OutreachTracker from './pages/OutreachTracker';

function App() {
  // Initialize user state directly from localStorage (synchronous)
  // This prevents the flash redirect on page load
  const [user, setUser] = useState(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      try {
        return JSON.parse(userData);
      } catch {
        return null;
      }
    }
    return null;
  });
  
  const [loading, setLoading] = useState(true);

  // Function to sync user state from localStorage
  const syncUserFromStorage = useCallback(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      try {
        const parsed = JSON.parse(userData);
        setUser(prevUser => {
          // Only update if data actually changed (prevents unnecessary re-renders)
          if (JSON.stringify(prevUser) !== JSON.stringify(parsed)) {
            return parsed;
          }
          return prevUser;
        });
      } catch {
        setUser(null);
      }
    } else {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    // Sync on mount (in case localStorage changed)
    syncUserFromStorage();
    setLoading(false);

    // Listen for storage changes (e.g., from other tabs or manual updates)
    const handleStorageChange = (e) => {
      if (e.key === 'user' || e.key === 'token') {
        syncUserFromStorage();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [syncUserFromStorage]);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <Router>
      <AppContent user={user} setUser={setUser} login={login} logout={logout} syncUserFromStorage={syncUserFromStorage} />
    </Router>
  );
}

// Separate component to use useLocation hook (must be inside Router)
function AppContent({ user, setUser, login, logout, syncUserFromStorage }) {
  const location = useLocation();

  // Re-sync user from localStorage when route changes (catches KYC verification redirect)
  useEffect(() => {
    syncUserFromStorage();
  }, [location.pathname, syncUserFromStorage]);

  return (
    <div className="App">
      <Navbar user={user} logout={logout} />
      <Routes>
        <Route path="/" element={<Home user={user} />} />
        <Route path="/login" element={user ? <Navigate to={`/${user.userType}-portal`} /> : <Login login={login} />} />
        <Route path="/signup" element={user ? <Navigate to={`/${user.userType}-portal`} /> : <Signup login={login} />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/welcome" element={<WelcomeScreen />} />
        <Route path="/verification-complete" element={<VerificationComplete />} />
        <Route path="/offer/:slug" element={<PublicOfferPage />} />
        <Route path="/listings" element={<Listings user={user} />} />
        <Route path="/listings/:id" element={<ListingDetails user={user} />} />
        <Route 
          path="/make-offer/:id" 
          element={user ? <BuyerOfferForm /> : <Navigate to="/login" />} 
        />
        <Route 
          path="/buyer-portal" 
          element={user && user.userType === 'buyer' ? <BuyerPortal user={user} /> : <Navigate to="/login" />} 
        />
        <Route 
          path="/seller-portal" 
          element={user && user.userType === 'seller' ? <SellerPortal user={user} /> : <Navigate to="/login" />} 
        />
        <Route 
          path="/attorney-portal" 
          element={user && user.userType === 'attorney' ? <AttorneyPortal user={user} /> : <Navigate to="/login" />} 
        />
        <Route path="/third-party-portal" element={user && user.userType === "third_party" ? <ThirdPartyPortal user={user} /> : <Navigate to="/login" />} />
        <Route path="/mls-agent-portal" element={user && user.userType === "mls_agent" ? <MLSAgentPortal user={user} /> : <Navigate to="/login" />} />
        <Route path="/admin/outreach" element={user ? <OutreachTracker user={user} /> : <Navigate to="/login" />} />
      </Routes>
    </div>
  );
}

export default App;
